import React,{useState,useEffect,useCallback,useRef} from 'react';
import {productAPI,brandAPI,uploadAPI,vendorAPI} from '../utils/api';
import {PageHeader,SearchBar,DataTable,Badge,Modal,FormInput,fmt,FilterButtons} from '../components/common';
import {useLang} from '../i18n';
import {Plus,Edit,Trash2,ToggleLeft,ToggleRight,Image,Ruler,X,Star,Pencil,Upload,FileSpreadsheet,Download,CheckCircle,AlertCircle,AlertTriangle,ChevronDown,ChevronUp,RefreshCw,Printer,Tag} from 'lucide-react';
import ProductLabelModal from '../components/ProductLabelModal';
import toast from 'react-hot-toast';
import {useAuth} from '../context/AuthContext';

// ── SheetJS loader (CDN, no npm needed) ──────────────────────────
let XLSX_LIB = null;
function loadXLSX(){
  return new Promise((resolve,reject)=>{
    if(XLSX_LIB){resolve(XLSX_LIB);return;}
    if(window.XLSX){XLSX_LIB=window.XLSX;resolve(XLSX_LIB);return;}
    const s=document.createElement('script');
    s.src='https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
    s.onload=()=>{XLSX_LIB=window.XLSX;resolve(XLSX_LIB);}
    s.onerror=()=>reject(new Error('Failed to load XLSX library'));
    document.head.appendChild(s);
  });
}

// Column map: excel header → internal field key
const COL_MAP = {
  'sku':'sku','★ sku':'sku',
  'name (en)':'name','★ name (en)':'name','name':'name',
  'name (kh)':'name_kh','name kh':'name_kh',
  '★ sale price':'sale_price','sale price':'sale_price','sale':'sale_price',
  'cost price':'cost_price','cost':'cost_price',
  'wholesale price':'wholesale_price','wholesale':'wholesale_price','ws':'wholesale_price',
  'min price':'min_price',
  '★ category':'category','category':'category',
  'brand':'brand',
  'base uom':'uom','uom':'uom',
  'barcode':'barcode',
  'reorder level':'reorder_level','reorder':'reorder_level',
  'safety stock':'safety_stock',
  'tax %':'tax_rate','tax':'tax_rate',
  'commission %':'commission_rate','commission':'commission_rate','comm':'commission_rate',
  'track stock':'track_stock',
  'allow neg stock':'allow_negative_stock','allow negative stock':'allow_negative_stock',
  'description':'description',
  '★ active':'is_active','active':'is_active',
  'weight (kg)':'weight','weight':'weight',
};

function parseSheet(XLSX, buffer){
  const wb = XLSX.read(buffer,{type:'array'});
  // Find "Products Import" sheet or use first
  const sheetName = wb.SheetNames.find(n=>n.toLowerCase().includes('import'))||wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const raw = XLSX.utils.sheet_to_json(ws,{header:1,defval:''});

  // Find header row: look for a row containing 'sku'
  let headerRowIdx = -1;
  for(let i=0;i<Math.min(raw.length,10);i++){
    const cells = raw[i].map(c=>String(c).toLowerCase().replace(/[^a-z0-9 %★◎]/g,'').trim());
    if(cells.some(c=>c.includes('sku'))){headerRowIdx=i;break;}
  }
  if(headerRowIdx===-1) throw new Error('Cannot find header row. Make sure it contains a "SKU" column.');

  const headers = raw[headerRowIdx].map(c=>String(c).toLowerCase().replace(/[^a-z0-9 %★◎]/g,'').trim());
  const colKeys = headers.map(h=>COL_MAP[h]||null);

  // Parse rows after header, skip blank and example rows
  const rows=[];
  for(let i=headerRowIdx+1;i<raw.length;i++){
    const r=raw[i];
    // Skip if first populated cell is a note/hint row (italic hint row)
    const firstVal=String(r[0]||'').trim();
    if(!firstVal||firstVal.startsWith('Instructions')||firstVal.startsWith('e.g.')||firstVal.startsWith('Unique product')) continue;
    const obj={_row:i+1};
    let hasData=false;
    colKeys.forEach((key,ci)=>{
      if(!key) return;
      const v=r[ci];
      obj[key]=v!==undefined&&v!==null&&v!==''?String(v).trim():'';
      if(key==='sku'&&obj[key]) hasData=true;
    });
    if(hasData) rows.push(obj);
  }
  return rows;
}

function validateRows(rows){
  return rows.map(r=>({
    ...r,
    _errors:[
      !r.sku?'SKU required':null,
      !r.name?'Name (EN) required':null,
      (r.sale_price===''||r.sale_price===undefined)?'Sale Price required':null,
      (r.sale_price&&isNaN(parseFloat(r.sale_price)))?'Sale Price must be a number':null,
    ].filter(Boolean),
  }));
}

// ── Import Modal Component ────────────────────────────────────────
function ImportModal({open,onClose,onSuccess,cats,uoms,brands}){
  const[step,setStep]=useState('upload'); // upload | preview | result
  const[dragging,setDragging]=useState(false);
  const[parsing,setParsing]=useState(false);
  const[rows,setRows]=useState([]);
  const[result,setResult]=useState(null);
  const[importing,setImporting]=useState(false);
  const[showErrors,setShowErrors]=useState(true);
  const[showAll,setShowAll]=useState(false);
  const fileRef=useRef();

  const reset=()=>{setStep('upload');setRows([]);setResult(null);setParsing(false);setImporting(false);setShowAll(false);};
  const handleClose=()=>{reset();onClose();};

  const processFile=async(file)=>{
    if(!file) return;
    const ext=file.name.split('.').pop().toLowerCase();
    if(!['xlsx','xls','csv'].includes(ext)) return toast.error('Only .xlsx, .xls, .csv files supported');
    setParsing(true);
    try{
      const XLSX=await loadXLSX();
      const buf=await file.arrayBuffer();
      const parsed=parseSheet(XLSX,new Uint8Array(buf));
      if(parsed.length===0){toast.error('No data rows found');setParsing(false);return;}
      const validated=validateRows(parsed);
      setRows(validated);
      setStep('preview');
    }catch(e){toast.error(e.message||'Failed to parse file');}
    finally{setParsing(false);}
  };

  const handleDrop=e=>{e.preventDefault();setDragging(false);processFile(e.dataTransfer.files[0]);};
  const handleFile=e=>processFile(e.target.files[0]);

  const doImport=async()=>{
    const valid=rows.filter(r=>r._errors.length===0);
    if(valid.length===0) return toast.error('No valid rows to import');
    setImporting(true);
    try{
      const payload=valid.map(r=>({
        sku:r.sku,name:r.name,name_kh:r.name_kh||'',barcode:r.barcode||'',
        description:r.description||'',category:r.category||'',brand:r.brand||'',uom:r.uom||'',
        sale_price:parseFloat(r.sale_price)||0,cost_price:parseFloat(r.cost_price)||0,
        wholesale_price:parseFloat(r.wholesale_price)||0,min_price:parseFloat(r.min_price)||0,
        tax_rate:parseFloat(r.tax_rate)||0,commission_rate:parseFloat(r.commission_rate)||0,
        reorder_level:parseInt(r.reorder_level)||10,safety_stock:parseInt(r.safety_stock)||5,
        weight:r.weight?parseFloat(r.weight)||null:null,
        is_active:r.is_active!==undefined&&r.is_active!==''?r.is_active:'YES',
        allow_negative_stock:r.allow_negative_stock||'NO',
      }));
      const res=await productAPI.importBulk(payload);
      setResult(res.data);
      setStep('result');
      if(res.data.created+res.data.updated>0) onSuccess();
    }catch(e){toast.error(e.response?.data?.error||'Import failed');}
    finally{setImporting(false);}
  };

  const validRows=rows.filter(r=>r._errors.length===0);
  const errorRows=rows.filter(r=>r._errors.length>0);
  const displayRows=showAll?rows:rows.slice(0,50);

  const catNames=new Set(cats.map(c=>c.name.toLowerCase()));
  const uomNames=new Set([...uoms.map(u=>u.name.toLowerCase()),...uoms.map(u=>u.abbreviation.toLowerCase())]);
  const brandNames=new Set(brands.map(b=>b.name.toLowerCase()));

  const warnRow=r=>{
    const w=[];
    if(r.category&&!catNames.has(r.category.toLowerCase())) w.push(`Category "${r.category}" not found`);
    if(r.uom&&!uomNames.has(r.uom.toLowerCase())) w.push(`UoM "${r.uom}" not found`);
    if(r.brand&&!brandNames.has(r.brand.toLowerCase())) w.push(`Brand "${r.brand}" not found`);
    return w;
  };

  if(!open) return null;

  const S={
    overlay:{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16},
    modal:{background:'var(--surface)',borderRadius:14,border:'1px solid var(--border)',width:'100%',maxWidth:900,maxHeight:'90vh',display:'flex',flexDirection:'column',overflow:'hidden'},
    hdr:{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'18px 22px',borderBottom:'1px solid var(--border)',flexShrink:0},
    body:{overflowY:'auto',padding:'20px 22px',flex:1},
    footer:{padding:'14px 22px',borderTop:'1px solid var(--border)',display:'flex',gap:10,justifyContent:'flex-end',flexShrink:0},
  };

  return(
    <div style={S.overlay} onClick={e=>{if(e.target===e.currentTarget)handleClose();}}>
      <div style={S.modal}>
        {/* Header */}
        <div style={S.hdr}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <FileSpreadsheet size={20} color="var(--primary)"/>
            <div>
              <div style={{fontWeight:700,fontSize:15}}>
                {step==='upload'&&'Import Products from Excel'}
                {step==='preview'&&`Preview — ${rows.length} rows (${validRows.length} valid, ${errorRows.length} errors)`}
                {step==='result'&&'Import Complete'}
              </div>
              {step==='preview'&&<div style={{fontSize:11,color:'var(--td)',marginTop:1}}>Review data before importing</div>}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={handleClose}><X size={16}/></button>
        </div>

        {/* Step indicator */}
        <div style={{display:'flex',gap:0,padding:'0 22px',background:'var(--s2)',borderBottom:'1px solid var(--border)',flexShrink:0}}>
          {['upload','preview','result'].map((s,i)=>(
            <div key={s} style={{padding:'10px 18px',fontSize:12,fontWeight:600,color:step===s?'var(--primary)':'var(--td)',borderBottom:step===s?'2px solid var(--primary)':'2px solid transparent',cursor:'default',display:'flex',alignItems:'center',gap:6}}>
              <div style={{width:18,height:18,borderRadius:'50%',background:step===s?'var(--primary)':['result'].includes(step)&&i<2?'var(--success)':'var(--border)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:700,color:'var(--bg)',flexShrink:0}}>
                {(['result'].includes(step)&&i<2)?'✓':(i+1)}
              </div>
              {s==='upload'?'Upload File':s==='preview'?'Preview & Validate':'Result'}
            </div>
          ))}
        </div>

        <div style={S.body}>
          {/* ── STEP 1: Upload ── */}
          {step==='upload'&&(
            <div>
              {/* Drop zone */}
              <div
                onDragOver={e=>{e.preventDefault();setDragging(true);}}
                onDragLeave={()=>setDragging(false)}
                onDrop={handleDrop}
                style={{border:`2px dashed ${dragging?'var(--primary)':'var(--border)'}`,borderRadius:12,padding:'48px 32px',textAlign:'center',cursor:'pointer',background:dragging?'rgba(0,212,170,0.05)':'var(--s2)',transition:'all .2s',marginBottom:20}}
                onClick={()=>fileRef.current?.click()}
              >
                <FileSpreadsheet size={40} color={dragging?'var(--primary)':'var(--td)'} style={{margin:'0 auto 12px'}}/>
                <div style={{fontWeight:700,fontSize:15,marginBottom:6}}>{parsing?'Parsing file...':'Drop your Excel file here'}</div>
                <div style={{color:'var(--td)',fontSize:13,marginBottom:14}}>or click to browse</div>
                <div style={{display:'inline-flex',alignItems:'center',gap:6,background:'var(--primary)',color:'var(--bg)',padding:'8px 20px',borderRadius:8,fontWeight:600,fontSize:13}}>
                  <Upload size={15}/> Choose File
                </div>
                <div style={{color:'var(--td)',fontSize:11,marginTop:12}}>Supports .xlsx · .xls · .csv — max 10MB</div>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} style={{display:'none'}}/>
              </div>

              {/* Tips */}
              <div style={{background:'var(--s2)',borderRadius:10,padding:16,border:'1px solid var(--border)'}}>
                <div style={{fontWeight:700,fontSize:12,color:'var(--tm)',marginBottom:10,letterSpacing:.5}}>TIPS</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px 20px'}}>
                  {[
                    ['Required columns','SKU, Name (EN), Sale Price, Category, Active'],
                    ['Optional columns','Cost, Wholesale, UoM, Brand, Barcode, Tax, Commission, etc.'],
                    ['YES / NO fields','Active, Track Stock, Allow Negative Stock'],
                    ['Duplicate SKUs','Existing SKUs will be updated, new ones created'],
                    ['Categories & UoM','Must match existing names in your system'],
                    ['Example rows','Any rows with SKU "LAP-001" or "WAT-012" are treated as real data'],
                  ].map(([k,v])=>(
                    <div key={k} style={{fontSize:12,display:'flex',gap:6,alignItems:'flex-start'}}>
                      <span style={{color:'var(--primary)',flexShrink:0}}>▸</span>
                      <span><strong style={{color:'var(--text)'}}>{k}:</strong> <span style={{color:'var(--td)'}}>{v}</span></span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 2: Preview ── */}
          {step==='preview'&&(
            <div>
              {/* Summary bar */}
              <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
                <div style={{flex:1,minWidth:120,background:'rgba(0,212,170,0.1)',border:'1px solid rgba(0,212,170,0.3)',borderRadius:8,padding:'10px 14px',textAlign:'center'}}>
                  <div style={{fontSize:20,fontWeight:700,color:'var(--success)'}}>{validRows.length}</div>
                  <div style={{fontSize:11,color:'var(--td)'}}>Valid rows</div>
                </div>
                <div style={{flex:1,minWidth:120,background:errorRows.length>0?'rgba(255,68,68,0.1)':'var(--s2)',border:`1px solid ${errorRows.length>0?'rgba(255,68,68,0.3)':'var(--border)'}`,borderRadius:8,padding:'10px 14px',textAlign:'center'}}>
                  <div style={{fontSize:20,fontWeight:700,color:errorRows.length>0?'var(--danger)':'var(--td)'}}>{errorRows.length}</div>
                  <div style={{fontSize:11,color:'var(--td)'}}>Errors (will skip)</div>
                </div>
                <div style={{flex:1,minWidth:120,background:'var(--s2)',border:'1px solid var(--border)',borderRadius:8,padding:'10px 14px',textAlign:'center'}}>
                  <div style={{fontSize:20,fontWeight:700,color:'var(--text)'}}>{rows.length}</div>
                  <div style={{fontSize:11,color:'var(--td)'}}>Total rows</div>
                </div>
              </div>

              {/* Error panel */}
              {errorRows.length>0&&(
                <div style={{background:'rgba(255,68,68,0.08)',border:'1px solid rgba(255,68,68,0.25)',borderRadius:8,marginBottom:14}}>
                  <button onClick={()=>setShowErrors(v=>!v)} style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',background:'none',border:'none',cursor:'pointer',color:'var(--danger)'}}>
                    <span style={{fontWeight:700,fontSize:12,display:'flex',alignItems:'center',gap:6}}><AlertCircle size={14}/> {errorRows.length} row(s) have errors — will be skipped</span>
                    {showErrors?<ChevronUp size={14}/>:<ChevronDown size={14}/>}
                  </button>
                  {showErrors&&<div style={{padding:'0 14px 12px'}}>
                    {errorRows.map(r=>(
                      <div key={r._row} style={{fontSize:11,color:'var(--danger)',marginBottom:4,display:'flex',gap:8}}>
                        <span style={{fontFamily:'monospace',minWidth:60}}>Row {r._row}</span>
                        <span style={{color:'var(--td)',minWidth:80}}>{r.sku||'—'}</span>
                        <span>{r._errors.join(' · ')}</span>
                      </div>
                    ))}
                  </div>}
                </div>
              )}

              {/* Data table preview */}
              <div style={{overflowX:'auto',borderRadius:8,border:'1px solid var(--border)'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead>
                    <tr style={{background:'var(--s2)'}}>
                      {['Row','SKU','Name (EN)','Category','Brand','UoM','Sale','Cost','Active','Status'].map(h=>(
                        <th key={h} style={{padding:'8px 10px',textAlign:'left',fontWeight:700,color:'var(--tm)',borderBottom:'1px solid var(--border)',whiteSpace:'nowrap',fontSize:11}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayRows.map((r,i)=>{
                      const warns=warnRow(r);
                      const hasErr=r._errors.length>0;
                      return(
                        <tr key={i} style={{background:hasErr?'rgba(255,68,68,0.05)':i%2===0?'var(--surface)':'var(--s2)',borderBottom:'1px solid var(--border)'}}>
                          <td style={{padding:'6px 10px',color:'var(--td)'}}>{r._row}</td>
                          <td style={{padding:'6px 10px',fontFamily:'monospace',color:'var(--primary)',fontSize:11}}>{r.sku||<span style={{color:'var(--danger)'}}>MISSING</span>}</td>
                          <td style={{padding:'6px 10px',maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.name||<span style={{color:'var(--danger)'}}>MISSING</span>}</td>
                          <td style={{padding:'6px 10px'}}>
                            <span style={{color:r.category&&!catNames.has(r.category.toLowerCase())?'var(--warning)':'var(--text)'}}>{r.category||<span style={{color:'var(--td)'}}>—</span>}</span>
                          </td>
                          <td style={{padding:'6px 10px'}}>
                            <span style={{color:r.brand&&!brandNames.has(r.brand.toLowerCase())?'var(--warning)':'var(--text)'}}>{r.brand||<span style={{color:'var(--td)'}}>—</span>}</span>
                          </td>
                          <td style={{padding:'6px 10px'}}>
                            <span style={{color:r.uom&&!uomNames.has(r.uom.toLowerCase())?'var(--warning)':'var(--text)'}}>{r.uom||<span style={{color:'var(--td)'}}>—</span>}</span>
                          </td>
                          <td style={{padding:'6px 10px',textAlign:'right'}}>{r.sale_price?`$${parseFloat(r.sale_price).toFixed(2)}`:''}</td>
                          <td style={{padding:'6px 10px',textAlign:'right',color:'var(--td)'}}>{r.cost_price?`$${parseFloat(r.cost_price).toFixed(2)}`:''}</td>
                          <td style={{padding:'6px 10px'}}>
                            <Badge variant={String(r.is_active||'YES').toLowerCase()==='yes'||String(r.is_active||'YES').toLowerCase()==='true'?'success':'danger'} style={{fontSize:10}}>
                              {String(r.is_active||'YES').toUpperCase()==='YES'||String(r.is_active||'YES').toLowerCase()==='true'?'YES':'NO'}
                            </Badge>
                          </td>
                          <td style={{padding:'6px 10px'}}>
                            {hasErr?(
                              <span style={{display:'flex',alignItems:'center',gap:4,color:'var(--danger)',fontSize:11}}><AlertCircle size={12}/>{r._errors[0]}</span>
                            ):warns.length>0?(
                              <span style={{display:'flex',alignItems:'center',gap:4,color:'var(--warning)',fontSize:11}} title={warns.join('\n')}><AlertTriangle size={12}/>Warning</span>
                            ):(
                              <span style={{display:'flex',alignItems:'center',gap:4,color:'var(--success)',fontSize:11}}><CheckCircle size={12}/>OK</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {rows.length>50&&!showAll&&(
                  <div style={{textAlign:'center',padding:10,borderTop:'1px solid var(--border)',background:'var(--s2)'}}>
                    <button className="btn btn-ghost btn-sm" onClick={()=>setShowAll(true)}>Show all {rows.length} rows</button>
                  </div>
                )}
              </div>

              {/* Warning about yellow cells */}
              {rows.some(r=>warnRow(r).length>0&&r._errors.length===0)&&(
                <div style={{display:'flex',gap:8,alignItems:'flex-start',padding:'10px 14px',background:'rgba(255,165,0,0.08)',border:'1px solid rgba(255,165,0,0.25)',borderRadius:8,marginTop:12,fontSize:12,color:'var(--warning)'}}>
                  <AlertTriangle size={14} style={{flexShrink:0,marginTop:1}}/>
                  <div><strong>Yellow cells</strong>: Category, Brand, or UoM name not found in system — those fields will be left blank on import. You can fix them after import.</div>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 3: Result ── */}
          {step==='result'&&result&&(
            <div>
              <div style={{textAlign:'center',padding:'24px 0 20px'}}>
                <div style={{width:64,height:64,borderRadius:'50%',background:result.errors>0?'rgba(255,165,0,0.15)':'rgba(0,212,170,0.15)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 14px'}}>
                  {result.errors>0?<AlertTriangle size={30} color="var(--warning)"/>:<CheckCircle size={30} color="var(--success)"/>}
                </div>
                <div style={{fontWeight:700,fontSize:18,marginBottom:6}}>
                  {result.errors>0?'Import Completed with Warnings':'Import Successful!'}
                </div>
                <div style={{color:'var(--td)',fontSize:13}}>Products have been saved to the system</div>
              </div>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:20}}>
                {[
                  {label:'Created',value:result.created,color:'var(--success)',icon:'✚'},
                  {label:'Updated',value:result.updated,color:'var(--primary)',icon:'↻'},
                  {label:'Errors',value:result.errors,color:result.errors>0?'var(--danger)':'var(--td)',icon:'✕'},
                ].map(({label,value,color,icon})=>(
                  <div key={label} style={{background:'var(--s2)',borderRadius:10,padding:'16px',textAlign:'center',border:'1px solid var(--border)'}}>
                    <div style={{fontSize:28,fontWeight:800,color}}>{value}</div>
                    <div style={{fontSize:12,color:'var(--td)',marginTop:2}}>{label}</div>
                  </div>
                ))}
              </div>

              {result.details?.errors?.length>0&&(
                <div style={{background:'rgba(255,68,68,0.08)',border:'1px solid rgba(255,68,68,0.25)',borderRadius:8,padding:14,marginBottom:14}}>
                  <div style={{fontWeight:700,fontSize:12,color:'var(--danger)',marginBottom:8}}>Failed rows:</div>
                  {result.details.errors.map((e,i)=>(
                    <div key={i} style={{fontSize:11,color:'var(--danger)',marginBottom:3,display:'flex',gap:10}}>
                      <span style={{fontFamily:'monospace',minWidth:50}}>Row {e.row}</span>
                      <span style={{color:'var(--td)',minWidth:80}}>{e.sku||'—'}</span>
                      <span>{e.error}</span>
                    </div>
                  ))}
                </div>
              )}

              {result.details?.updated?.length>0&&(
                <div style={{background:'rgba(0,212,170,0.05)',border:'1px solid rgba(0,212,170,0.2)',borderRadius:8,padding:14}}>
                  <div style={{fontWeight:700,fontSize:12,color:'var(--primary)',marginBottom:8}}>Updated ({result.details.updated.length}):</div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                    {result.details.updated.map((u,i)=>(
                      <span key={i} style={{background:'rgba(0,212,170,0.1)',padding:'2px 8px',borderRadius:4,fontSize:11,fontFamily:'monospace'}}>{u.sku}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={S.footer}>
          {step==='upload'&&<button className="btn btn-ghost" onClick={handleClose}>{t('common.cancel')}</button>}
          {step==='preview'&&(
            <>
              <button className="btn btn-ghost" onClick={()=>{setStep('upload');setRows([]);}}>← Back</button>
              <button className="btn btn-ghost" onClick={handleClose}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={doImport} disabled={validRows.length===0||importing} style={{minWidth:140}}>
                {importing?<><RefreshCw size={14} style={{animation:'spin 1s linear infinite'}}/> Importing…</>:<><Upload size={14}/> Import {validRows.length} Products</>}
              </button>
            </>
          )}
          {step==='result'&&(
            <>
              <button className="btn btn-ghost" onClick={()=>{reset();}}>Import Another</button>
              <button className="btn btn-primary" onClick={handleClose}>Done</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main ProductsPage ─────────────────────────────────────────────
export default function ProductsPage(){
  const{t}=useLang();
  const{isOwner,myBranchId,myBranch,hasPermission}=useAuth();
  const canSeeCost=hasPermission('cost_price.view');
  const[data,setData]=useState([]);const[cats,setCats]=useState([]);const[uoms,setUoms]=useState([]);const[brands,setBrands]=useState([]);const[vendors,setVendors]=useState([]);
  const[ld,setLd]=useState(true);const[search,setSearch]=useState('');const[catFilter,setCatFilter]=useState('All');
  const[modal,setModal]=useState(null);const[form,setForm]=useState({});const[delId,setDelId]=useState(null);
  const[labelModal,setLabelModal]=useState(null); // null | 'all' | product obj for single
  const[selProduct,setSelProduct]=useState(null);const[imgModal,setImgModal]=useState(false);const[imgUrl,setImgUrl]=useState('');
  const[uomModal,setUomModal]=useState(false);const[prodUoms,setProdUoms]=useState([]);
  const[uomForm,setUomForm]=useState({uom_id:'',conversion_factor:1,price:0,barcode:''});
  const[prodUomPrices,setProdUomPrices]=useState([]);
  const[prodImages,setProdImages]=useState([]);const[editImgId,setEditImgId]=useState(null);const[editImgUrl,setEditImgUrl]=useState('');
  const[importOpen,setImportOpen]=useState(false);

  const loadMeta=async()=>{try{const[c,u,b,v]=await Promise.all([productAPI.getCategories(),productAPI.getUoms(),brandAPI.getAll({limit:200}),vendorAPI.getAll({limit:200})]);setCats(c.data.data||c.data);setUoms(u.data.data||u.data);setBrands(b.data.data||b.data);setVendors(v.data.data||v.data)}catch{}}; 
  const load=useCallback(async()=>{try{const p={search,limit:100};if(catFilter!=='All'){const c=cats.find(x=>x.name===catFilter);if(c)p.category_id=c.id}const r=await productAPI.getAll(p);setData(r.data.data)}catch{toast.error(t('common.failedToSave'))}finally{setLd(false)}},[search,catFilter,cats]);
  useEffect(()=>{loadMeta()},[]);
  useEffect(()=>{const t=setTimeout(load,300);return()=>clearTimeout(t)},[load]);

  const catOpts=[{value:'',label:'— No Category —'},...cats.map(c=>({value:String(c.id),label:c.name}))];
  const uomOpts=[{value:'',label:'— No UoM —'},...uoms.map(u=>({value:String(u.id),label:`${u.name} (${u.abbreviation})`}))];
  const brandOpts=[{value:'',label:'— No Brand —'},...brands.map(b=>({value:String(b.id),label:b.name}))];
  const vendorOpts=[{value:'',label:'— No Default Vendor —'},...vendors.map(v=>({value:String(v.id),label:v.name}))];
  const catFilters=['All',...cats.map(c=>c.name)];
  const blank={sku:'',barcode:'',name:'',name_kh:'',description:'',category_id:'',brand_id:'',uom_id:'',vendor_id:'',sale_price:0,cost_price:0,wholesale_price:0,min_price:0,tax_rate:0,commission_rate:0,reorder_level:10,safety_stock:5,weight:'',image_url:'',expiration_tracking:false,serial_tracking:false,batch_tracking:false,allow_negative_stock:false,is_active:true,emenu_popularity:null,emenu_featured:false};
  const F=(k,v)=>setForm(f=>({...f,[k]:v}));

  const saveUomPrices=async(productId)=>{
    for(const row of prodUomPrices){
      if(!row.uom_id)continue;
      const payload={uom_id:row.uom_id,conversion_factor:parseFloat(row.factor)||1,price:parseFloat(row.price)||0,barcode:row.barcode||''};
      try{
        if(row.puId)await productAPI.updateUomConversion(productId,row.puId,payload);
        else if(parseFloat(row.price)>0)await productAPI.addUomConversion(productId,payload);
      }catch(e){console.warn('UoM price save failed',e);}
    }
  };
  const loadInlineUomPrices=async(product)=>{
    if(!product?.id){setProdUomPrices([]);return;}
    try{
      const r=await productAPI.getUomConversions(product.id);
      const existing=r.data||[];
      setProdUomPrices(existing.map(rec=>({
        puId:rec.id,uom_id:rec.uom_id,
        uom_name:rec.uom?.name||'',uom_abbr:rec.uom?.abbreviation||'',
        price:parseFloat(rec.price)||0,factor:parseFloat(rec.conversion_factor)||1,barcode:rec.barcode||''
      })));
    }catch{setProdUomPrices([]);}
  };
  const save=async()=>{
    if(!form.sku||!form.name)return toast.error(t('products.skuNameRequired'));
    try{const d={...form,sale_price:parseFloat(form.sale_price)||0,cost_price:parseFloat(form.cost_price)||0,wholesale_price:parseFloat(form.wholesale_price)||0,min_price:parseFloat(form.min_price)||0,tax_rate:parseFloat(form.tax_rate)||0,commission_rate:parseFloat(form.commission_rate)||0,reorder_level:parseInt(form.reorder_level)||10,safety_stock:parseInt(form.safety_stock)||5,weight:parseFloat(form.weight)||null,category_id:parseInt(form.category_id)||null,uom_id:parseInt(form.uom_id)||null,brand_id:parseInt(form.brand_id)||null,emenu_popularity:form.emenu_popularity?parseInt(form.emenu_popularity):null,emenu_featured:!!form.emenu_featured};
    let savedId=form.id;
    if(modal==='new'){const r=await productAPI.create(d);savedId=r.data?.id||r.data?.data?.id;toast.success(t('common.created'))}else{await productAPI.update(form.id,d);toast.success(t('common.updated'))}
    if(savedId)await saveUomPrices(savedId);
    setModal(null);load()}catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}};
  const toggle=async p=>{try{await productAPI.update(p.id,{is_active:!p.is_active});toast.success(p.is_active?'Deactivated':'Activated');load()}catch{toast.error(t('common.failedToSave'))}};
  const del=async()=>{try{await productAPI.delete(delId);toast.success(t('common.deleted'));setDelId(null);load()}catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}};

  // Images
  const openImages=async p=>{setSelProduct(p);setProdImages(p.images||[]);setImgUrl('');setEditImgId(null);setImgModal(true)};
  const[imgUploading,setImgUploading]=useState(false);
  const addImage=async()=>{if(!imgUrl)return toast.error(t('products.urlRequired'));try{await productAPI.addImage(selProduct.id,{url:imgUrl,is_primary:prodImages.length===0});toast.success(t('common.created'));const r=await productAPI.getById(selProduct.id);setProdImages(r.data.images||[]);setImgUrl('');load()}catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}};
  const uploadImage=async(e)=>{const file=e.target.files?.[0];if(!file)return;if(file.size>5*1024*1024)return toast.error(t('common.max5mb'));setImgUploading(true);try{const r=await uploadAPI.single(file);const url=r.data.url;await productAPI.addImage(selProduct.id,{url,is_primary:prodImages.length===0});toast.success(t('products.uploadedAdded'));const pr=await productAPI.getById(selProduct.id);setProdImages(pr.data.images||[]);load()}catch(e2){toast.error(e2.response?.data?.error||'Upload failed')}finally{setImgUploading(false);e.target.value=''}};
  const updateImage=async(id)=>{if(!editImgUrl)return;try{await productAPI.updateImage(selProduct.id,id,{url:editImgUrl});toast.success(t('common.updated'));const r=await productAPI.getById(selProduct.id);setProdImages(r.data.images||[]);setEditImgId(null);setEditImgUrl('');load()}catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}};
  const removeImage=async id=>{try{await productAPI.removeImage(selProduct.id,id);toast.success(t('common.deleted'));setProdImages(p=>p.filter(i=>i.id!==id));load()}catch{toast.error(t('common.failedToSave'))}};
  const setPrimary=async id=>{try{await productAPI.setPrimaryImage(selProduct.id,id);toast.success(t('common.saved'));setProdImages(p=>p.map(i=>({...i,is_primary:i.id===id})));load()}catch{toast.error(t('common.failedToSave'))}};

  // UoM conversions
  const openUoms=async p=>{setSelProduct(p);try{const r=await productAPI.getUomConversions(p.id);setProdUoms(r.data)}catch{setProdUoms(p.uoms||[])}setUomForm({uom_id:uoms[0]?.id||'',conversion_factor:1,price:0,barcode:''});setUomModal(true)};
  const addUom=async()=>{if(!uomForm.uom_id)return toast.error(t('products.selectUom'));try{await productAPI.addUomConversion(selProduct.id,{...uomForm,uom_id:parseInt(uomForm.uom_id),conversion_factor:parseFloat(uomForm.conversion_factor)||1,price:parseFloat(uomForm.price)||0});toast.success(t('common.created'));const r=await productAPI.getUomConversions(selProduct.id);setProdUoms(r.data);setUomForm({uom_id:'',conversion_factor:1,price:0,barcode:''})}catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}};
  const removeUom=async id=>{try{await productAPI.removeUomConversion(selProduct.id,id);toast.success(t('common.deleted'));setProdUoms(p=>p.filter(u=>u.id!==id))}catch{toast.error(t('common.failedToSave'))}};

  return(<div>
    <PageHeader title={t('products.title')} subtitle={`${data.length} products`} action={
      <div style={{display:'flex',gap:8}}>
        <button className="btn btn-ghost" onClick={()=>setLabelModal('all')} style={{display:'flex',alignItems:'center',gap:6}}>
          <Tag size={15}/> Print Labels
        </button>
        <button className="btn btn-ghost" onClick={()=>setImportOpen(true)} style={{display:'flex',alignItems:'center',gap:6}}>
          <FileSpreadsheet size={15}/> Import Excel
        </button>
        <button className="btn btn-primary" onClick={()=>{setForm(blank);setProdUomPrices([]);setModal('new')}}>
          <Plus size={16}/> Add Product
        </button>
      </div>
    }/>
    <div className="filter-bar"><div className="flex-1" style={{minWidth:200}}><SearchBar value={search} onChange={setSearch} placeholder={t('products.searchPlaceholder')}/></div><FilterButtons options={catFilters} active={catFilter} onChange={setCatFilter}/></div>

    <DataTable loading={ld} columns={[
      {key:'img',label:'',render:r=>r.image_url?<img src={r.image_url} alt="" style={{width:36,height:36,borderRadius:6,objectFit:'cover',border:'1px solid var(--border)'}}/>:<div style={{width:36,height:36,borderRadius:6,background:'var(--s2)',display:'flex',alignItems:'center',justifyContent:'center'}}><Image size={14} color="var(--td)"/></div>},
      {key:'sku',label:'SKU',render:r=><span className="text-accent" style={{fontFamily:'monospace',fontSize:12}}>{r.sku}</span>},
      {key:'name',label:t('common.product'),render:r=><div><div className="font-bold">{r.name}</div>{r.name_kh&&<div style={{fontSize:10,color:'var(--td)'}}>{r.name_kh}</div>}<div style={{fontSize:11,color:'var(--td)'}}>{r.category?.name||''}{r.brand?.name?` · ${r.brand.name}`:''}</div></div>},
      {key:'barcode',label:t('products.barcode'),render:r=>r.barcode||'—'},
      {key:'brand',label:t('products.brand'),render:r=>r.brand?<div className="flex items-center gap-2">{r.brand.logo_url&&<img src={r.brand.logo_url} alt="" style={{width:18,height:18,objectFit:'contain'}}/>}<span style={{fontSize:12}}>{r.brand.name}</span></div>:<span className="text-dim">—</span>},
      {key:'vendor',label:'Default Vendor',render:r=>r.vendor?<span style={{fontSize:12,color:'var(--tm)'}}>{r.vendor.name}</span>:<span className="text-dim">—</span>},
      {key:'uom',label:'UoM',render:r=><span>{r.uom?.abbreviation||'—'}{(r.uoms?.length||0)>0&&<Badge variant="info" style={{marginLeft:4}}>+{r.uoms.length}</Badge>}</span>},
      {key:'sale_price',label:t('products.salePrice'),render:r=><span className="font-bold">{fmt(r.sale_price)}</span>},
      ...(canSeeCost?[
        {key:'cost_price',label:t('products.costPrice'),render:r=><span style={{color:'var(--td)'}}>{fmt(r.cost_price)}</span>},
        {key:'wholesale',label:'WS',render:r=>fmt(r.wholesale_price)},
        {key:'margin',label:t('products.margin'),render:r=>{const m=r.sale_price>0?(((r.sale_price-r.cost_price)/r.sale_price)*100).toFixed(1):0;return<Badge variant={m>40?'success':m>20?'warning':'danger'}>{m}%</Badge>}},
      ]:[]),
      {key:'comm',label:t('products.commission'),render:r=>r.commission_rate?`${r.commission_rate}%`:'—'},
      {key:'track',label:t('common.analytics'),render:r=><div style={{display:'flex',gap:2}}>{r.expiration_tracking&&<Badge variant="warning">EXP</Badge>}{r.serial_tracking&&<Badge variant="info">SN</Badge>}{r.batch_tracking&&<Badge variant="accent">LOT</Badge>}</div>},
      {key:'imgs',label:t('common.image'),render:r=><span className="text-muted">{r.images?.length||0}</span>},
      {key:'is_active',label:'',render:r=><Badge variant={r.is_active?'success':'danger'}>{r.is_active?'ON':'OFF'}</Badge>},
    ]} data={data} actions={r=>(<div className="flex gap-2">
      <button className="btn btn-ghost btn-sm" onClick={()=>setLabelModal([r])} title="Print Label"><Tag size={13}/></button>
      <button className="btn btn-ghost btn-sm" onClick={()=>openImages(r)} title="Images"><Image size={14}/></button>
      <button className="btn btn-ghost btn-sm" onClick={()=>openUoms(r)} title="UoM"><Ruler size={14}/></button>
      <button className="btn btn-ghost btn-sm" onClick={()=>toggle(r)}>{r.is_active?<ToggleRight size={16} color="var(--success)"/>:<ToggleLeft size={16}/>}</button>
      <button className="btn btn-ghost btn-sm" onClick={()=>{const p={...r,category_id:r.category_id||r.category?.id||'',uom_id:r.uom_id||r.uom?.id||'',brand_id:r.brand_id||r.brand?.id||''};setForm(p);loadInlineUomPrices(p);setModal('edit')}}><Edit size={14}/></button>
      <button className="btn btn-danger btn-sm" onClick={()=>setDelId(r.id)}><Trash2 size={14}/></button>
    </div>)}/>

    {/* LABEL PRINT MODAL */}
    {labelModal&&<ProductLabelModal
      products={labelModal==='all'?data:(Array.isArray(labelModal)?labelModal:[])}
      onClose={()=>setLabelModal(null)}
    />}

    {/* IMPORT MODAL */}
    <ImportModal
      open={importOpen}
      onClose={()=>setImportOpen(false)}
      onSuccess={()=>{load();toast.success('Products imported successfully!');}}
      cats={cats} uoms={uoms} brands={brands}
    />

    {/* CREATE/EDIT */}
    <Modal open={modal==='new'||modal==='edit'} onClose={()=>setModal(null)} title={modal==='new'?'Add Product':'Edit Product'} width={700}>
      <div style={{display:'flex',gap:16,marginBottom:16,alignItems:'flex-start'}}>
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:6,flexShrink:0}}>
          <div style={{width:110,height:110,borderRadius:10,border:'2px dashed var(--border)',display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden',background:'var(--s2)',position:'relative',cursor:'pointer'}}>
            {form.image_url?<img src={form.image_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>:<div style={{textAlign:'center'}}><Upload size={24} color="var(--td)"/><div className="text-dim" style={{fontSize:9,marginTop:4}}>Upload Image</div></div>}
            <input type="file" accept="image/*" onChange={async e=>{const file=e.target.files?.[0];if(!file)return;if(file.size>5*1024*1024)return toast.error(t('common.max5mb'));try{const r=await uploadAPI.single(file);F('image_url',r.data.url);toast.success(t('common.uploaded'))}catch{toast.error(t('common.uploadFailed'))}e.target.value=''}} style={{position:'absolute',inset:0,opacity:0,cursor:'pointer'}}/>
          </div>
          {form.image_url&&<button className="btn btn-ghost btn-sm" onClick={()=>F('image_url','')} style={{fontSize:10}}>{t('common.remove')}</button>}
        </div>
        <div style={{flex:1}}>
          <FormInput label={t('products.imageUrl')} value={form.image_url||''} onChange={v=>F('image_url',v)} placeholder="https://..."/>
          <div className="form-grid" style={{marginTop:0}}>
            <FormInput label='SKU' value={form.sku||''} onChange={v=>F('sku',v)} required/><FormInput label={t('products.barcode')} value={form.barcode||''} onChange={v=>F('barcode',v)}/>
          </div>
        </div>
      </div>
      <div className="form-grid">
        <FormInput label={t('products.nameEn')} value={form.name||''} onChange={v=>F('name',v)} required/><FormInput label={t('products.nameKh')} value={form.name_kh||''} onChange={v=>F('name_kh',v)}/>
        <FormInput label={t('products.brand')} value={String(form.brand_id||'')} onChange={v=>F('brand_id',v)} options={brandOpts}/><FormInput label={t('products.category')} value={String(form.category_id||'')} onChange={v=>F('category_id',v)} options={catOpts}/>
        <FormInput label={t('products.baseUnit')} value={String(form.uom_id||'')} onChange={v=>F('uom_id',v)} options={uomOpts}/><FormInput label={t('products.weightKg')} value={String(form.weight||'')} onChange={v=>F('weight',v)} type="number"/>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 20px',marginBottom:0}}>
        <div style={{padding:14,background:'var(--s2)',borderRadius:10,marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:700,color:'var(--tm)',marginBottom:10,letterSpacing:.4}}>PURCHASE INFORMATION</div>
          <FormInput label="Default Vendor" value={String(form.vendor_id||'')} onChange={v=>F('vendor_id',v)} options={vendorOpts}/>
          {canSeeCost&&<FormInput label={t('products.costPrice')} value={String(form.cost_price||0)} onChange={v=>F('cost_price',v)} type="number"/>}
          <FormInput label={t('products.taxPercent')} value={String(form.tax_rate||0)} onChange={v=>F('tax_rate',v)} type="number"/>
          <FormInput label={t('products.commissionPercent')} value={String(form.commission_rate||0)} onChange={v=>F('commission_rate',v)} type="number"/>
          {canSeeCost&&<FormInput label={t('products.wholesalePrice')} value={String(form.wholesale_price||0)} onChange={v=>F('wholesale_price',v)} type="number"/>}
          {canSeeCost&&<FormInput label={t('products.minPrice')} value={String(form.min_price||0)} onChange={v=>F('min_price',v)} type="number"/>}
        </div>
        <div style={{padding:14,background:'var(--s2)',borderRadius:10,marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:700,color:'var(--tm)',marginBottom:10,letterSpacing:.4}}>SALE INFORMATION</div>
          <FormInput label={t('products.salePrice')} value={String(form.sale_price||0)} onChange={v=>F('sale_price',v)} type="number"/>
          <div style={{marginTop:12}}>
            <div style={{fontSize:11,fontWeight:700,color:'var(--tm)',marginBottom:8,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <span>REGULAR PRICE BY UNIT</span>
              <button type="button" className="btn btn-ghost btn-sm" style={{fontSize:10,padding:'2px 8px'}}
                onClick={()=>{const baseUomId=parseInt(form.uom_id)||null;const newRow={puId:null,uom_id:baseUomId,uom_name:'',uom_abbr:'',price:parseFloat(form.sale_price)||0,factor:1,barcode:''};setProdUomPrices(arr=>[...arr,newRow]);}}>
                + Add Row
              </button>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 100px 34px',gap:6,marginBottom:4}}>
              <div style={{fontSize:10,color:'var(--td)',fontWeight:700,paddingLeft:4}}>UNIT MEASURE</div>
              <div style={{fontSize:10,color:'var(--td)',fontWeight:700,textAlign:'right',paddingRight:4}}>NEW PRICE</div>
              <div/>
            </div>
            {prodUomPrices.length===0&&(
              <div style={{fontSize:11,color:'var(--td)',textAlign:'center',padding:'10px 0',background:'var(--surface)',borderRadius:6,border:'1px dashed var(--border)'}}>
                No unit prices. Add rows or select a UoM.
              </div>
            )}
            {prodUomPrices.map((row,i)=>(
              <div key={i} style={{display:'grid',gridTemplateColumns:'1fr 100px 34px',gap:6,marginBottom:6,alignItems:'center'}}>
                <select className="form-select" value={String(row.uom_id||'')}
                  onChange={e=>{const u=uoms.find(x=>x.id===parseInt(e.target.value));setProdUomPrices(arr=>{const n=[...arr];n[i]={...n[i],uom_id:parseInt(e.target.value)||null,uom_name:u?.name||'',uom_abbr:u?.abbreviation||''};return n;});}}
                  style={{fontSize:12,background:'var(--surface)'}}>
                  <option value="">— Select —</option>
                  {uoms.map(u=><option key={u.id} value={u.id}>{u.name} ({u.abbreviation})</option>)}
                </select>
                <input type="number" min={0} step={0.01} className="form-input"
                  value={row.price??0}
                  onChange={e=>setProdUomPrices(arr=>{const n=[...arr];n[i]={...n[i],price:parseFloat(e.target.value)||0};return n;})}
                  style={{textAlign:'right',fontSize:13}}/>
                <button type="button" className="btn btn-ghost btn-sm"
                  style={{color:'var(--danger)',padding:'4px',width:32,justifyContent:'center'}}
                  onClick={async()=>{if(row.puId&&form.id){try{await productAPI.removeUomConversion(form.id,row.puId)}catch{}}setProdUomPrices(arr=>arr.filter((_,j)=>j!==i));}}>
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{padding:12,background:'var(--s2)',borderRadius:10,marginBottom:14}}>
        <div style={{fontSize:11,fontWeight:700,color:'var(--tm)',marginBottom:8}}>STOCK CONTROL</div>
        <div className="form-grid">
          <FormInput label={t('products.reorderLevel')} value={String(form.reorder_level||10)} onChange={v=>F('reorder_level',v)} type="number"/><FormInput label={t('products.safetyStock')} value={String(form.safety_stock||5)} onChange={v=>F('safety_stock',v)} type="number"/>
          <FormInput label={t('products.expiryTracking')} value={form.expiration_tracking?'true':'false'} onChange={v=>F('expiration_tracking',v==='true')} options={[{value:'false',label:t('common.no')},{value:'true',label:t('common.yes')}]}/>
          <FormInput label={t('products.serialTracking')} value={form.serial_tracking?'true':'false'} onChange={v=>F('serial_tracking',v==='true')} options={[{value:'false',label:t('common.no')},{value:'true',label:t('common.yes')}]}/>
          <FormInput label={t('products.batchLotTracking')} value={form.batch_tracking?'true':'false'} onChange={v=>F('batch_tracking',v==='true')} options={[{value:'false',label:t('common.no')},{value:'true',label:t('common.yes')}]}/>
          <FormInput label={t('products.allowNegativeStock')} value={form.allow_negative_stock?'true':'false'} onChange={v=>F('allow_negative_stock',v==='true')} options={[{value:'false',label:t('common.no')},{value:'true',label:t('common.yes')}]}/>
        </div>
      </div>
      {/* E-Menu Settings */}
      <div style={{background:'var(--s2)',borderRadius:10,padding:'14px 16px',border:'1px solid var(--border)',marginBottom:12}}>
        <div style={{fontSize:11,fontWeight:700,color:'var(--tm)',marginBottom:8,display:'flex',alignItems:'center',gap:6}}>
          <span>⭐</span> E-MENU SETTINGS <span style={{fontSize:10,fontWeight:400,color:'var(--td)'}}>(optional)</span>
        </div>
        <div className="form-grid">
          <div>
            <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:5}}>
              Popularity / Rating <span style={{color:'var(--td)',fontWeight:400}}>(1–100)</span>
            </label>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <input type="number" min={1} max={100} className="form-input"
                value={form.emenu_popularity||''}
                onChange={e=>F('emenu_popularity',e.target.value?parseInt(e.target.value):null)}
                placeholder="Leave blank to hide"
                style={{flex:1}}/>
              {form.emenu_popularity>0&&(
                <div style={{display:'flex',alignItems:'center',gap:4,padding:'4px 10px',borderRadius:20,fontSize:11,fontWeight:800,
                  background:form.emenu_popularity>=80?'#ef4444':form.emenu_popularity>=60?'#f59e0b':'#6366f1',color:'#fff',whiteSpace:'nowrap'}}>
                  ⭐ {form.emenu_popularity}
                </div>
              )}
            </div>
            <div style={{fontSize:10,color:'var(--td)',marginTop:4}}>Shows as a badge on the e-menu card. 80+ = red (top), 60+ = amber, below = purple.</div>
          </div>
          <div>
            <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:5}}>
              Featured / Hot Item
            </label>
            <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',padding:'10px 12px',borderRadius:8,border:`1.5px solid ${form.emenu_featured?'var(--warning)':'var(--border)'}`,background:form.emenu_featured?'rgba(245,158,11,0.08)':'transparent',transition:'all .15s'}}>
              <input type="checkbox" checked={!!form.emenu_featured} onChange={e=>F('emenu_featured',e.target.checked)}
                style={{width:16,height:16,accentColor:'var(--warning)',cursor:'pointer'}}/>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:form.emenu_featured?'var(--warning)':'var(--text)'}}>
                  {form.emenu_featured?'⚡ Featured — shown first':'Mark as Hot / Featured'}
                </div>
                <div style={{fontSize:10,color:'var(--td)',marginTop:1}}>Pins this item to the top of the e-menu</div>
              </div>
            </label>
          </div>
        </div>
      </div>
      <FormInput label={t('common.description')} value={form.description||''} onChange={v=>F('description',v)} type="textarea"/>
      <div className="modal-footer"><button className="btn btn-ghost" onClick={()=>setModal(null)}>{t('common.cancel')}</button><button className="btn btn-primary" onClick={save}>{t('common.save')}</button></div>
    </Modal>

    {/* IMAGE GALLERY */}
    <Modal open={imgModal} onClose={()=>{setImgModal(false);setEditImgId(null)}} title={`Images — ${selProduct?.name||''}`} width={620}>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:10,marginBottom:16}}>
        {prodImages.map(img=>(
          <div key={img.id} style={{borderRadius:8,overflow:'hidden',border:`2px solid ${img.is_primary?'var(--primary)':'var(--border)'}`,background:'var(--s2)'}}>
            <div style={{position:'relative'}}>
              <img src={img.url} alt="" style={{width:'100%',height:100,objectFit:'cover'}}/>
              {img.is_primary&&<div style={{position:'absolute',top:4,left:4}}><Badge variant="primary">★ Primary</Badge></div>}
            </div>
            {editImgId===img.id?(
              <div style={{padding:6}}>
                <input className="form-input" value={editImgUrl} onChange={e=>setEditImgUrl(e.target.value)} placeholder="New URL..." style={{fontSize:11,padding:'4px 8px',marginBottom:4}}/>
                <div className="flex gap-2">
                  <button className="btn btn-primary btn-sm" style={{flex:1,justifyContent:'center',fontSize:10}} onClick={()=>updateImage(img.id)}>{t('common.save')}</button>
                  <button className="btn btn-ghost btn-sm" style={{fontSize:10}} onClick={()=>{setEditImgId(null);setEditImgUrl('')}}>✕</button>
                </div>
              </div>
            ):(
              <div style={{display:'flex',justifyContent:'space-between',padding:'4px 6px'}}>
                {!img.is_primary&&<button className="btn btn-ghost btn-sm" onClick={()=>setPrimary(img.id)} title="Set primary"><Star size={12}/></button>}
                {img.is_primary&&<span/>}
                <button className="btn btn-ghost btn-sm" onClick={()=>{setEditImgId(img.id);setEditImgUrl(img.url)}} title="Edit URL"><Pencil size={12}/></button>
                <button className="btn btn-danger btn-sm" onClick={()=>removeImage(img.id)} title={t('common.remove')}><Trash2 size={12}/></button>
              </div>
            )}
          </div>))}
        {prodImages.length===0&&<p className="text-dim" style={{gridColumn:'1/-1',textAlign:'center',padding:20}}>No images — add one below</p>}
      </div>
      <div style={{padding:14,background:'var(--s2)',borderRadius:10}}>
        <div style={{fontSize:11,fontWeight:700,color:'var(--tm)',marginBottom:8,letterSpacing:.4}}>ADD IMAGE</div>
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          <label className="btn btn-primary" style={{cursor:'pointer',position:'relative'}}>
            <Upload size={14}/> Upload File
            <input type="file" accept="image/*" onChange={uploadImage} style={{position:'absolute',inset:0,opacity:0,cursor:'pointer'}}/>
          </label>
          {imgUploading&&<span className="text-dim" style={{fontSize:12}}>Uploading...</span>}
          <span className="text-dim" style={{fontSize:12}}>or paste URL:</span>
          <input className="form-input" value={imgUrl} onChange={e=>setImgUrl(e.target.value)} placeholder='https://...' style={{flex:1,minWidth:180}}/>
          <button className="btn btn-ghost" onClick={addImage} disabled={!imgUrl}><Plus size={14}/> Add URL</button>
        </div>
      </div>
    </Modal>

    {/* UOM CONVERSIONS */}
    <Modal open={uomModal} onClose={()=>setUomModal(false)} title={`UoM Conversions — ${selProduct?.name||''}`} width={600}>
      <p style={{fontSize:12,color:'var(--tm)',marginBottom:12}}>Base: <strong>{selProduct?.uom?.name||'Piece'} ({selProduct?.uom?.abbreviation||'pcs'})</strong> — 1 box = 12 pcs means factor = 12</p>
      {prodUoms.length>0&&<DataTable columns={[
        {key:'uom',label:'Unit',render:r=><span className="font-bold">{r.uom?.name||'—'} ({r.uom?.abbreviation||''})</span>},
        {key:'factor',label:'= Base × Factor',render:r=><span className="text-primary font-bold">{r.conversion_factor}</span>},
        {key:'price',label:'Price/Unit',render:r=>r.price?fmt(r.price):'—'},
        {key:'barcode',label:t('products.barcode'),render:r=>r.barcode||'—'},
      ]} data={prodUoms} actions={r=>(<button className="btn btn-danger btn-sm" onClick={()=>removeUom(r.id)}><X size={14}/></button>)}/>}
      {prodUoms.length===0&&<p className="text-dim" style={{textAlign:'center',padding:16}}>No conversions</p>}
      <div style={{padding:14,background:'var(--s2)',borderRadius:10,marginTop:12}}>
        <div style={{fontSize:11,fontWeight:700,color:'var(--tm)',marginBottom:8}}>ADD CONVERSION</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 12px'}}>
          <FormInput label={t('products.uom')} value={String(uomForm.uom_id||'')} onChange={v=>setUomForm(f=>({...f,uom_id:v}))} options={uomOpts}/>
          <FormInput label={t('products.conversionFactor')} value={String(uomForm.conversion_factor)} onChange={v=>setUomForm(f=>({...f,conversion_factor:v}))} type="number"/>
          <FormInput label={t('products.pricePerThisUnit')} value={String(uomForm.price||0)} onChange={v=>setUomForm(f=>({...f,price:v}))} type="number"/>
          <FormInput label={t('products.barcode')} value={uomForm.barcode||''} onChange={v=>setUomForm(f=>({...f,barcode:v}))}/>
        </div>
        <div style={{textAlign:'right'}}><button className="btn btn-primary" onClick={addUom}>{t('common.add')}</button></div>
      </div>
    </Modal>

    {/* DELETE */}
    <Modal open={!!delId} onClose={()=>setDelId(null)} title={t('products.deleteProduct')} width={400}>
      <p className="text-muted" style={{marginBottom:18}}>Delete permanently?</p>
      <div className="modal-footer"><button className="btn btn-ghost" onClick={()=>setDelId(null)}>{t('common.cancel')}</button><button className="btn btn-danger" onClick={del}>{t('common.delete')}</button></div>
    </Modal>
  </div>);
}
